export interface AppErrorContext {
    [key: string]: any;
}

const ERROR_MESSAGE_KEYS = ['message', 'error', 'detail', 'description', 'error_description'] as const;

/**
 * Extract a human-readable message from Error instances and nested API error
 * envelopes without ever leaking JavaScript's unhelpful "[object Object]".
 */
export function getErrorMessage(value: unknown, fallback = 'An unexpected error occurred'): string {
    const seen = new Set<object>();

    const visit = (candidate: unknown, depth: number): string | null => {
        if (typeof candidate === 'string') {
            const trimmed = candidate.trim();
            return trimmed || null;
        }
        if (candidate instanceof Error) return visit(candidate.message, depth + 1);
        if (!candidate || typeof candidate !== 'object' || depth > 4 || seen.has(candidate)) return null;

        seen.add(candidate);
        const record = candidate as Record<string, unknown>;
        for (const key of ERROR_MESSAGE_KEYS) {
            const message = visit(record[key], depth + 1);
            if (message) return message;
        }
        return null;
    };

    return visit(value, 0) || fallback;
}

export class AppError extends Error {
    public readonly code: string;
    public readonly status: number;
    public readonly isOperational: boolean;
    public readonly context?: AppErrorContext;

    constructor(
        message: string,
        code: string = 'INTERNAL_ERROR',
        status: number = 500,
        isOperational: boolean = true,
        context?: AppErrorContext
    ) {
        super(message);
        this.name = 'AppError';
        this.code = code;
        this.status = status;
        this.isOperational = isOperational;
        this.context = context;

        // Capture stack trace
        const errConstructor = Error as any;
        if (errConstructor.captureStackTrace) {
            errConstructor.captureStackTrace(this, this.constructor);
        }
    }

    public toJSON() {
        return {
            name: this.name,
            message: this.message,
            code: this.code,
            status: this.status,
            isOperational: this.isOperational,
            context: this.context,
        };
    }
}
